import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import {
  OrderNumberingStrategyDateFormat,
  OrderNumberingStrategyResetFrequency,
  OrderNumberingStrategyType,
  Restaurant,
} from "@/redux/services/restaurant/restaurant.type";
import { useEditRestaurant } from "@/hooks/parametres/useEditRestaurant";
import { Select } from "@/components/Select";
import Switch from "@/components/ui/switch";
import { Error } from "@/components/Error";
import { Hash, Receipt, Settings, Save, Store } from "lucide-react";
import CollapsibleSection from "@/components/parametres/CollapsibleSection";
import { orderNumberPreview } from "@/lib/orderNumberPreview";

const orderNumberingStrategyTypes = [
  { label: "Date", value: OrderNumberingStrategyType.Date },
  { label: "Séquentiel", value: OrderNumberingStrategyType.Sequential },
];

const orderNumberingStrategyResetFrequency = [
  {
    label: "Tous les jours",
    value: OrderNumberingStrategyResetFrequency.Daily,
  },
  {
    label: "Chaque semaine",
    value: OrderNumberingStrategyResetFrequency.Weekly,
  },
  {
    label: "Chaque mois",
    value: OrderNumberingStrategyResetFrequency.Monthly,
  },
  {
    label: "Chaque année",
    value: OrderNumberingStrategyResetFrequency.Yearly,
  },
];

const orderNumberingStrategyDateFormat = [
  {
    label: "DDMMYYYY",
    value: OrderNumberingStrategyDateFormat.DDMMYYYY,
  },
  {
    label: "MMDDYYYY",
    value: OrderNumberingStrategyDateFormat.MMDDYYYY,
  },
  {
    label: "YYYYMMDD",
    value: OrderNumberingStrategyDateFormat.YYYYMMDD,
  },
  {
    label: "DDMMYYYYHHMM",
    value: OrderNumberingStrategyDateFormat.DDMMYYYYHHMM,
  },
  {
    label: "MMDDYYYYHHMM",
    value: OrderNumberingStrategyDateFormat.MMDDYYYYHHMM,
  },
  {
    label: "YYYYMMDDHHMM",
    value: OrderNumberingStrategyDateFormat.YYYYMMDDHHMM,
  },
];

const InfoRestaurant = ({
  item,
  canManage = true,
}: {
  item?: Restaurant;
  // When false (a settings.view delegate without settings.manage), the profile
  // is read-only: switches and the save action are disabled.
  canManage?: boolean;
}) => {
  const {
    register,
    onSubmit,
    errors,
    isLoading,
    watch,
    handleShouldAskForWaiterName,
    shouldAskForWaiterName,
    handleShouldAskForGuestCount,
    shouldAskForGuestCount,
    handleAllowOrderPlacement,
    allowOrderPlacement,
    handleCollectCustomerDataUpfront,
    collectCustomerDataUpfront,
    handleTrackIngredientStock,
    trackIngredientStock,
  } = useEditRestaurant(item);

  const type = watch("settings.orderNumberingStrategy.type");
  const dateFormat = watch("settings.orderNumberingStrategy.dateFormat");
  const prefix = watch("settings.orderNumberingStrategy.prefix");
  const padding = watch("settings.orderNumberingStrategy.padding");
  const startNumber = watch("settings.orderNumberingStrategy.startNumber");
  const exampleOrderNumber = orderNumberPreview({
    type,
    dateFormat,
    prefix,
    padding,
    startNumber,
  });

  const poPrefix = watch("settings.purchaseOrderNumbering.prefix");
  const poPadding = watch("settings.purchaseOrderNumbering.padding");
  const examplePoNumber = `${poPrefix ?? "BC-"}${String(1).padStart(
    Number(poPadding) || 6,
    "0"
  )}`;


  return (
    <form onSubmit={onSubmit} className="space-y-4 [&_input]:py-2 [&_input]:text-sm">
      {/* Restaurant info */}
      <CollapsibleSection icon={Store} title="Informations du restaurant">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Nom du restaurant"
              type="text"
              id="name"
              placeholder="Nom du restaurant"
              defaultValue={item?.name}
              {...register("name")}
              error={errors.name}
            />
          </div>
          <div>
            <Input
              label="Adresse du restaurant"
              type="text"
              id="adresse"
              placeholder="Adresse du restaurant"
              defaultValue={item?.address}
              {...register("address")}
              error={errors.address}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Order numbering strategy */}
      <CollapsibleSection icon={Hash} title="Stratégie de numérotation des commandes">
        <div className="bg-muted rounded-lg px-4 py-3 mb-4">
          <p className="text-sm text-muted-foreground">
            Aperçu: <span className="font-bold text-primary text-base">{exampleOrderNumber}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Select
              label="Type"
              id="order_numbering_strategy_type"
              {...register("settings.orderNumberingStrategy.type")}
              error={errors?.settings?.orderNumberingStrategy?.type}
              options={orderNumberingStrategyTypes}
              defaultValue={item?.settings?.orderNumberingStrategy?.type}
            />
          </div>
          <div>
            <Select
              label="Fréquence de réinitialisation"
              id="order_numbering_strategy_reset_frequency"
              {...register("settings.orderNumberingStrategy.resetFrequency")}
              error={errors?.settings?.orderNumberingStrategy?.resetFrequency}
              options={orderNumberingStrategyResetFrequency}
              defaultValue={
                item?.settings?.orderNumberingStrategy?.resetFrequency
              }
            />
          </div>
          <div>
            <Select
              label="Format de date"
              id="order_numbering_strategy_date_format"
              {...register("settings.orderNumberingStrategy.dateFormat")}
              error={errors?.settings?.orderNumberingStrategy?.dateFormat}
              options={orderNumberingStrategyDateFormat}
              defaultValue={item?.settings?.orderNumberingStrategy?.dateFormat}
            />
          </div>
          <div>
            <Input
              label="Préfixe"
              type="text"
              id="order_numbering_strategy_prefix"
              placeholder="Ex: SF-"
              {...register("settings.orderNumberingStrategy.prefix")}
              error={errors?.settings?.orderNumberingStrategy?.prefix}
              defaultValue={item?.settings?.orderNumberingStrategy?.prefix}
            />
          </div>
          <div>
            <Input
              label="Zéros devant (0001)"
              type="number"
              id="order_numbering_strategy_padding"
              placeholder="Ex: 2"
              {...register("settings.orderNumberingStrategy.padding")}
              error={errors?.settings?.orderNumberingStrategy?.padding}
              defaultValue={item?.settings?.orderNumberingStrategy?.padding}
              min={2}
            />
          </div>
          <div>
            <Input
              label="Numéro de départ"
              type="number"
              id="order_numbering_strategy_start_number"
              placeholder="Ex: 1"
              {...register("settings.orderNumberingStrategy.startNumber")}
              error={errors?.settings?.orderNumberingStrategy?.startNumber}
              defaultValue={item?.settings?.orderNumberingStrategy?.startNumber}
              min={1}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Purchase order numbering */}
      <CollapsibleSection
        icon={Receipt}
        title="Numérotation des bons de commande"
      >
        <div className="bg-muted rounded-lg px-4 py-3 mb-4">
          <p className="text-sm text-muted-foreground">
            Aperçu:{" "}
            <span className="font-bold text-primary text-base">
              {examplePoNumber}
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Input
              label="Préfixe"
              type="text"
              id="purchase_order_numbering_prefix"
              placeholder="Ex: BC-"
              {...register("settings.purchaseOrderNumbering.prefix")}
              error={errors?.settings?.purchaseOrderNumbering?.prefix}
              defaultValue={item?.settings?.purchaseOrderNumbering?.prefix}
            />
          </div>
          <div>
            <Input
              label="Zéros devant (000001)"
              type="number"
              id="purchase_order_numbering_padding"
              placeholder="Ex: 6"
              {...register("settings.purchaseOrderNumbering.padding")}
              error={errors?.settings?.purchaseOrderNumbering?.padding}
              defaultValue={item?.settings?.purchaseOrderNumbering?.padding}
              min={1}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Les numéros existants ne sont pas renumérotés — la séquence se poursuit
          simplement avec le nouveau préfixe.
        </p>
      </CollapsibleSection>

      {/* Other settings */}
      <CollapsibleSection icon={Settings} title="Autres paramètres">

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <p className="text-sm font-medium text-card-foreground">
                Demander le nom du serveur
              </p>
              <p className="text-xs text-muted-foreground">
                Lors de la prise de commande
              </p>
            </div>
            <Switch
              id="should_ask_for_waiter_name"
              checked={shouldAskForWaiterName}
              onChange={handleShouldAskForWaiterName}
              disabled={!canManage}
            />
          </div>
          <Error error={errors?.settings?.shouldAskForWaiterName} />

          <div className="border-t border-border" />

          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <p className="text-sm font-medium text-card-foreground">
                Demander le nombre de couverts
              </p>
              <p className="text-xs text-muted-foreground">
                Sur les commandes sur place
              </p>
            </div>
            <Switch
              id="should_ask_for_guest_count"
              checked={shouldAskForGuestCount}
              onChange={handleShouldAskForGuestCount}
              disabled={!canManage}
            />
          </div>
          <Error error={errors?.settings?.shouldAskForGuestCount} />

          <div className="border-t border-border" />

          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <p className="text-sm font-medium text-card-foreground">
                Prise de commande en ligne
              </p>
              <p className="text-xs text-muted-foreground">
                Autoriser la prise de commande sur le menu digital
              </p>
            </div>
            <Switch
              id="allow_order_placement"
              checked={allowOrderPlacement}
              onChange={handleAllowOrderPlacement}
              disabled={!canManage}
            />
          </div>
          <Error error={errors?.settings?.allowOrderPlacement} />

          <div className="border-t border-border" />

          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <p className="text-sm font-medium text-card-foreground">
                Collecte de données client
              </p>
              <p className="text-xs text-muted-foreground">
                Collecter les données dès l'accès au menu (restaurants sans commande en ligne)
              </p>
            </div>
            <Switch
              id="collect_customer_data_upfront"
              checked={collectCustomerDataUpfront}
              onChange={handleCollectCustomerDataUpfront}
              disabled={!canManage}
            />
          </div>
          <Error error={errors?.settings?.collectCustomerDataUpfront} />

          <div className="border-t border-border" />

          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <p className="text-sm font-medium text-card-foreground">
                Suivi automatique du stock
              </p>
              <p className="text-xs text-muted-foreground">
                Déduire le stock des ingrédients à chaque commande
              </p>
            </div>
            <Switch
              id="track_ingredient_stock"
              checked={trackIngredientStock}
              onChange={handleTrackIngredientStock}
              disabled={!canManage}
            />
          </div>
          <Error error={errors?.settings?.trackIngredientStock} />
        </div>
      </CollapsibleSection>

      {canManage && (
        <div className="flex justify-end">
          <Button isLoading={isLoading} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            Enregistrer
          </Button>
        </div>
      )}
    </form>
  );
};

export default InfoRestaurant;
